import { Button, FormControl, Grid, Paper, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Theme, ThemeProvider, Tooltip, Typography, createTheme, useMediaQuery } from "@mui/material";
import React, { useState, useEffect, Fragment } from "react";
import { PageHeader } from "../../components/PageHeader";
import { useStyles } from "../../common/appStyles";
import { RootState } from "../../store/store";
import { useSelector } from "react-redux";
import { PageEnum, RoleEnum, Predicates } from "../../types/enums";
import { showSnackbar } from "../../services/snackbar.service";
import { GetUserForSchool } from "../../services/user-service";
import { IUsersForSchool } from "../../types/SchoolCoordinator";
import { useLocation, useNavigate } from "react-router-dom";
import { IExpressionRequest } from "../../types/expression";
import { RedirectToDash } from "../RedirectToDash";
import { Protoexpression } from "../../utilities/ProtoExpression";
import { BannerAd } from "../../components/main-layout/BannerAd";
/**
 * @remark School Coordinator Dashboard is used for viewing all user's competencies for a given school Id. {@link IUsersForSchool}
 * Users can use a search bar to find specific users, and click view to see detailed information about that user and their submissions.
 * @returns a react Html component with a table of all Users for a school.
 */
export const SchoolCoordinatorDashboard = () => {
    const selectedSchool = useLocation().state;
    const classes = useStyles();
    const navigate = useNavigate();
    const userRole = useSelector((state: RootState) => state.roles);
    const currentPage = userRole.userPageAccess[PageEnum.SchoolCoordinatorDashboard];
    const user = useSelector((state: RootState) => state.userDetail);

    const [pageTitle, setPageTitle] = useState("School Coordinator Dashboard");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [noResult, setNoResult] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [rows, setRows] = useState<IUsersForSchool[]>([]);
    const [filteredRows, setFilteredRows] = useState<IUsersForSchool[]>([]);
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchValue);
    const [count, setCount] = useState<number>(0);
    const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));

    const theme = createTheme({
        palette: {
            primary: {
                main: '#484A57'
            },
            secondary: {
                main: '#10AD61'
            },
            background: {
                default: '#ffffff'
            },
        },
        typography: {
            h6: {
                color: '#484A57'
            },
            body1: {
                color: '#00000'
            }
        },
    });
    const isSmScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const buildExpression = (query?: string, perPage?: number, pages?: number) => {
        let first: string | undefined;
        let last: string | undefined;
        if (query) {
            const trimmedAndLowercased = query.trim().toLowerCase();
            first = trimmedAndLowercased.split(' ').slice(0, -1).join(' ');
            last = trimmedAndLowercased.split(' ').slice(-1).join(' ');
        }

        //this needs to be refactored with pagination expression
        const expression: IExpressionRequest =
        {
            expressionRequest: {
                protoexpressions: [
                    Protoexpression("FirstName", first ? first : last ? last : "", Predicates.StartsWith),
                    Protoexpression("LastName", last ? first ? last : "" : "", Predicates.StartsWith)
                ]
            },
            paginationRequest: {
                entriesPerPage: perPage ? perPage : rowsPerPage,
                pageNumber: pages ? pages + 1 : page + 1
            }
        }
        return expression;
    }

    const getUserForSchool = (id, expression: IExpressionRequest) => {
        if (!id) {
            return;
        }
        GetUserForSchool(id, expression)
            .then(response => {
                let users = response.schoolCoordinatorDashboardInformation;
                setFilteredRows(users);
                setRows(users);
                checkNoFilteredResult(response.count);
                setCount(response.count);
            })
            .catch((error) => {
                showSnackbar('Error geting data', 'error', 5000);
            });
    }

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
        if (selectedSchool) {
            getUserForSchool(selectedSchool.schoolId, buildExpression(searchValue, rowsPerPage, newPage));
        }
        else {
            getUserForSchool(user.schoolId, buildExpression(searchValue, rowsPerPage, newPage));
        }
    }

    const handleChangeRowsPerPage = (value) => {
        setRowsPerPage(value);
        setPage(0);
        if (selectedSchool) {
            getUserForSchool(selectedSchool.schoolId, buildExpression(searchValue, value, 0));
        }
        else {
            getUserForSchool(user.schoolId, buildExpression(searchValue, value, 0));
        }
    }

    const handleSearch = (value) => {
        if (!value) {
            onSearch(value);
        }
    }

    useEffect(() => {
        const timerId = setTimeout(() => {
            setDebouncedSearchQuery(searchValue);
        }, 500);
        return () => clearTimeout(timerId);
    }, [searchValue]);

    useEffect(() => {
        if (debouncedSearchQuery) {
            onSearch(debouncedSearchQuery);
        }
    }, [debouncedSearchQuery]);

    const onSearch = (searchQuery) => {
        setPage(0);
        const expression = buildExpression(searchQuery);
        if (selectedSchool) {
            getUserForSchool(selectedSchool.schoolId, expression);
        }
        else {
            getUserForSchool(user.schoolId, expression);
        }
    }

    const checkNoFilteredResult = (check) => {
        setNoResult(check === 0);
    }

    const onView = async (row: IUsersForSchool) => {
        navigate(`/youthdashboard`, { state: { id: row.id } });
    };

    useEffect(() => {
        if (currentPage?.read) {
            if (!selectedSchool) {
                getUserForSchool(user.schoolId, buildExpression());
            }
            else {
                getUserForSchool(selectedSchool.schoolId, buildExpression());
            }
        }
    }, [currentPage, user, selectedSchool]);

    return (
        <Fragment>
            <ThemeProvider theme={theme}>
                <Grid
                    container
                    className={classes.formContainer}
                    spacing={1}
                    justifyContent="center"
                    alignItems={'center'}
                >
                    {user.roleId >= RoleEnum.CredentialingManager && !lgUp && <Grid className={classes.bannerTopPadding}>
                        <BannerAd />
                    </Grid>}
                    <Grid item sm={12} className='justify-center' padding="5px">
                        {currentPage?.read &&
                            <Grid
                                container
                                spacing={1}
                                direction={{ xs: 'column', md: 'row' }}
                                justifyContent="center"
                                alignItems="center"
                                className={classes.fixGridMargin}
                            >
                                <Grid item xs={4}><PageHeader title={pageTitle} onBackClick={undefined} /></Grid>
                                <><Grid item xs={12}>
                                    <Grid container justifyContent={"flex-start"} alignItems={"center"} direction={{ xs: 'column', md: 'row' }} >
                                        <Grid item xs={12} md={4.2}>
                                            {(Array.isArray(rows) && rows.length !== 0) ?
                                                <Grid container direction={"column"} className={isSmScreen ? '' : classes.gridLeftPadding}>
                                                    <Grid item >
                                                        <Typography variant="h6">
                                                            School: {rows[0]?.school}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                                :
                                                <Grid container direction={"column"} className={classes.gridLeftPadding}>
                                                    <Grid item >
                                                        <Typography variant="h6">
                                                            <Skeleton variant="text" width={'40vh'} />
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            }
                                        </Grid>
                                        <Grid item xs={3}>
                                            <FormControl variant='outlined' fullWidth>
                                                <Tooltip
                                                    title='Search for Youths by name'
                                                    placement="right"
                                                    arrow
                                                    slotProps={{
                                                        popper: {
                                                            modifiers: [
                                                                {
                                                                    name: 'offset',
                                                                    options: {
                                                                        offset: [0, -5],
                                                                    },
                                                                },
                                                            ],
                                                        },
                                                    }}
                                                >
                                                    <TextField
                                                        type="text"
                                                        label="Search Youth's Name"
                                                        aria-labelledby="Search textfield"
                                                        value={searchValue}
                                                        onChange={(e) => {
                                                            setSearchValue(e.target.value);
                                                            handleSearch(e.target.value);
                                                        }}
                                                    >
                                                    </TextField>
                                                </Tooltip>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </Grid><Grid
                                    container
                                    justifyContent={'center'}
                                    alignItems={"center"}
                                    spacing={1}
                                    className={classes.gridPaddingTop}
                                >
                                        <Grid item sm={11} className={classes.tableGrid} maxWidth={{ xs: '45vh', md: '100%' }}>
                                            {(Array.isArray(rows) && rows.length !== 0) ? <Paper elevation={3}>
                                                <TableContainer className={classes.tableContainer}>
                                                    <Table stickyHeader size="small" aria-label="school table">
                                                        <TableHead>
                                                            <TableRow className={classes.tableHead}>
                                                                <TableCell key="name">Youth</TableCell>
                                                                <TableCell key="GrowthMindset">Growth Mindset</TableCell>
                                                                <TableCell key="CreativeProblemSolving">Creative Problem Solving</TableCell>
                                                                <TableCell key="EmotionalIntelligence">Emotional Intelligence</TableCell>
                                                                <TableCell key="Coaching">Coaching</TableCell>
                                                                <TableCell key="SocialInfluence">Social Influence</TableCell>
                                                                {currentPage?.read && <TableCell key="actions" align="center">Actions</TableCell>}
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row: IUsersForSchool) => (
                                                                <TableRow
                                                                    key={row.id}
                                                                    className={classes.table}
                                                                >
                                                                    <TableCell component="th" scope="row">{row.lastName + ", " + row.firstName}</TableCell>
                                                                    <TableCell align="left">{row.competencyList
                                                                        .filter(competency => (competency.competencyId === 1))
                                                                        .map(({ submissionStage }) => submissionStage)
                                                                        .pop() || "Not Started"}
                                                                    </TableCell>
                                                                    <TableCell align="left">{row.competencyList
                                                                        .filter(competency => (competency.competencyId === 2))
                                                                        .map(({ submissionStage }) => submissionStage)
                                                                        .pop() || "Not Started"}
                                                                    </TableCell>
                                                                    <TableCell align="left">{row.competencyList
                                                                        .filter(competency => (competency.competencyId === 3))
                                                                        .map(({ submissionStage }) => submissionStage)
                                                                        .pop() || "Not Started"}
                                                                    </TableCell>
                                                                    <TableCell align="left">{row.competencyList
                                                                        .filter(competency => (competency.competencyId === 4))
                                                                        .map(({ submissionStage }) => submissionStage)
                                                                        .pop() || "Not Started"}
                                                                    </TableCell>
                                                                    <TableCell align="left">{row.competencyList
                                                                        .filter(competency => (competency.competencyId === 5))
                                                                        .map(({ submissionStage }) => submissionStage)
                                                                        .pop() || "Not Started"}
                                                                    </TableCell>
                                                                    <TableCell key="actions" align="center">
                                                                        {currentPage?.read &&
                                                                            <Button
                                                                                onClick={e => onView(row)}
                                                                                className="app-button ml10"
                                                                                color='primary'
                                                                                size="small"
                                                                                variant='contained'>
                                                                                View
                                                                            </Button>}
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                                <TablePagination
                                                    rowsPerPageOptions={[10, 25, 100]}
                                                    component="div"
                                                    count={count}
                                                    rowsPerPage={rowsPerPage}
                                                    page={page}
                                                    onPageChange={handleChangePage}
                                                    onRowsPerPageChange={(event) => handleChangeRowsPerPage(event.target.value)} />
                                            </Paper>
                                                :
                                                <Grid container direction={"column"} rowSpacing={1}>
                                                    <Grid item xs={12}>
                                                        <Skeleton variant='rectangular' width={"100%"} height={"2vh"} />
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <Skeleton variant='rounded' width={'100%'} height={'40vh'} />
                                                    </Grid>
                                                </Grid>
                                            }
                                        </Grid>
                                        <Grid alignSelf={"center"} className={classes.tableNoResult}>
                                            {noResult && <FormControl fullWidth><Typography variant="h5">No Results Found</Typography></FormControl>}
                                        </Grid>
                                    </Grid></>
                            </Grid>
                        }
                        {!currentPage?.read && currentPage?.read !== undefined &&
                            <RedirectToDash />
                        }
                    </Grid>
                </Grid>
            </ThemeProvider>
        </Fragment>);
}